<template>
  <model-form
    ref="modelForm"
    :model="deliveryLocation"
    modal-type="modal"
    :emit-only-create="deliveryLocation.type === DELIVERY_LOCATION_TYPES.TABLE"
    @create="create"
    @update:model-value="input => { if (!input) names = [] }"
  >
    <div class="column">
      <div class="row q-col-gutter-sm items-center">
        <q-select
          v-if="deliveryLocation.type === DELIVERY_LOCATION_TYPES.TABLE && $refs.modelForm && $refs.modelForm.creating"
          v-model="names"
          class="col-6"
          label="Names"
          :rules="[val => val.length > 0 || 'Field is required']"
          new-value-mode="add-unique"
          multiple
          use-input
          use-chips
          bottom-slots
          :autofocus="!$q.platform.is.ios"
          @keyup.enter.stop
        />
        <q-input
          v-else
          v-model="deliveryLocation.name"
          class="col-6"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          :autofocus="!$q.platform.is.ios"
        />
        <q-input
          v-model="deliveryLocation.shortName"
          class="col-6"
          label="Short Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          :autofocus="!$q.platform.is.ios"
        />
        <q-select
          v-model="deliveryLocation.type"
          class="col-6"
          label="Type"
          :options="DELIVERY_LOCATION_TYPES.ALL"
          :option-label="val => $_.startCase(val)"
          :display-value="$_.startCase(deliveryLocation.type)"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
          @update:model-value="generateNewPositions(deliveryLocation.generateNewPositions || !deliveryLocation.positions.length)"
        />
        <q-toggle
          v-model="deliveryLocation.allowOpenTabWithQrCode"
          label="Allow Open Tab With QR Code"
          class="col-6"
        />
        <q-select
          v-if="deliveryLocation.type === DELIVERY_LOCATION_TYPES.THEATER_SEAT"
          v-model="deliveryLocation.screen"
          class="col-12"
          label="Screen"
          :options="screens.data"
          option-label="name"
          use-input
          input-style="search"
          input-debounce="300"
          clearable
          bottom-slots
          :rules="[value => !$_.isNil(value) || 'Field is required']"
          @filter="filterScreens"
          @update:model-value="generateNewPositions(deliveryLocation.generateNewPositions || !deliveryLocation.positions.length)"
        />
        <q-toggle
          :model-value="deliveryLocation.generateNewPositions"
          class="col-6 q-pb-md"
          label="Generate New Positions"
          :disable="deliveryLocation.type === DELIVERY_LOCATION_TYPES.THEATER_SEAT && !deliveryLocation.screen"
          @update:model-value="val => generateNewPositions(val, true)"
        />
        <q-input
          v-model="deliveryLocation.positionsPrefix"
          class="col-6"
          label="Delivery Positions Prefix"
          bottom-slots
          debounce="300"
          @update:model-value="generateNewPositions(deliveryLocation.generateNewPositions || !deliveryLocation.positions.length)"
        />
        <q-select
          v-model="deliveryLocation.positions"
          class="col-12"
          style="max-width: 468px;"
          label="Delivery Positions"
          new-value-mode="add-unique"
          multiple
          use-input
          use-chips
          bottom-slots
          clearable
          @keyup.enter.stop
        />
      </div>
    </div>
  </model-form>
</template>

<script>

import DeliveryLocation, { DEFAULT_TABLE_POSITIONS, DELIVERY_LOCATION_TYPES } from 'src/models/delivery-location/DeliveryLocation'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const sortAlphaNum = (a, b) => a.localeCompare(b, 'en', { numeric: true })

export default {
  name: 'DeliveryLocationForm',
  components: {
    ModelForm
  },
  models: {
    deliveryLocation: {
      screen: {
        seatChart: {
          seatChart: true
        }
      },
      libraryChildSiteIds: true
    },
    screens: {
      fields: ['id', 'name'],
      seatChart: {
        fields: ['id', 'seatChart']
      },
      variables () {
        return {
          searchString: this.screenSearchString,
          // Most cinemas will not have many screens, so it's convenient to load (probably) all of them up front
          // This shouldn't use too much resource, but prevents issues if there are an excessive number of screens
          limit: 25
        }
      }
    }
  },
  data () {
    return {
      names: [],
      screenSearchString: null
    }
  },
  computed: {
    deliveryLocationSeatChart () {
      if (this.deliveryLocation.type !== DELIVERY_LOCATION_TYPES.THEATER_SEAT) return null

      if (_.isNil(this.deliveryLocation.screen)) return null

      // Find the screen in the loaded models, as the seatChart is not included in the ClientConfig
      // As DeliveryLocation Screen is a reference, it will always use the ClientConfig, so we cannot reference the seatChart through it
      const deliveryLocationScreen = _.find(this.screens.data, { id: this.deliveryLocation.screen.id })
      return deliveryLocationScreen?.seatChart?.seatChart
    }
  },
  watch: {
    'deliveryLocation.screen.id' () {
      if (this.deliveryLocation.type !== DELIVERY_LOCATION_TYPES.THEATER_SEAT) return

      if (_.isNil(this.deliveryLocation.screen)) return

      this.deliveryLocation.name = this.deliveryLocation.screen?.name
    }
  },
  created () {
    this.deliveryLocation.generateNewPositions = false

    _.extend(this, { DELIVERY_LOCATION_TYPES })
  },
  methods: {
    create () {
      _.each(this.names, async name => {
        // Actually do the saves
        const model = new DeliveryLocation({
          name,
          type: this.deliveryLocation.type,
          positionsPrefix: this.deliveryLocation.positionsPrefix,
          positions: this.deliveryLocation.positions
        })

        const result = await model.save()
        if (result.errors.length) {
          this.$mh.showErrors(result.errors)
        }
      })
    },
    filterScreens (val, update, _abort) {
      update(() => {
        this.screenSearchString = val
      })
    },
    generateNewPositions (generate, force = false) {
      if (generate && this.deliveryLocation.type === DELIVERY_LOCATION_TYPES.TABLE) {
        this.deliveryLocation.positions = _.map(DEFAULT_TABLE_POSITIONS, position => {
          return `${this.deliveryLocation.positionsPrefix || ''}${position}`
        })

        this.deliveryLocation.generateNewPositions = generate
      } else if (generate && this.deliveryLocation.type === DELIVERY_LOCATION_TYPES.THEATER_SEAT && !_.isNil(this.deliveryLocationSeatChart)) {
        this.deliveryLocation.positions = _(this.deliveryLocationSeatChart).map(row => {
          return _.map(row, seat => {
            if (!seat.name) return

            if (!seat.available) return

            return `${this.deliveryLocation.positionsPrefix || ''}${seat.name}`
          })
        }).flatten().compact().sort(sortAlphaNum).value()

        this.deliveryLocation.generateNewPositions = generate
      } else if (!generate && force) {
        this.deliveryLocation.positions = this.deliveryLocation.originalData.positions

        this.deliveryLocation.generateNewPositions = generate
      }
    }
  }
}

</script>
